import { City, LocationLoadingState, LocationOption, Location } from 'interfaces/locations'

export const DEFAULT_LOCATIONS_LOADING_STATE: LocationLoadingState = {
  loading: false,
  lastSentQuery: '',
  options: [],
}

export const cityToOption = (city: City): LocationOption => ({
  value: city.id,
  label: getCityStateLabel(city),
  type: 'city',
})

export const locationToOption = (location: Location): LocationOption => ({
  value: location.id,
  label: location.name,
  type: location.type,
})

/** Label string for a city/location autosuggest option (react-select `formatOptionLabel`). */
export function formatCityOption (option: LocationOption | null | undefined): string {
  if (option == null) {
    return ''
  }
  return option.label
}

/** Coerces API, query, or form values to a numeric city id. */
export function parseCityId (raw: unknown): number | null {
  if (raw === null || raw === undefined || raw === '') {
    return null
  }
  const n = typeof raw === 'number' ? raw : Number.parseInt(String(raw), 10)
  return Number.isFinite(n) ? n : null
}

const BIG_CITIES: { [key: string]: string } = {
  'New York, NY': 'We live in New York too!',
  'Los Angeles, CA': 'Hello City of Angels!',
  'Chicago, IL': 'Chicago is our kind of town!',
  'Houston, TX': 'Houston, we have liftoff!',
  'Philadelphia, PA': 'Hello city of brotherly love!',
  'Phoenix, AZ': 'Hello red rocks and blue sky!',
  'San Antonio, TX': 'Gotta love those Spurs!',
  'San Diego, CA': 'Holla to La Jolla!',
  'Dallas, TX': 'How ‘bout them Cowboys!',
  'San Jose, CA': 'Hello Silicon Valley!',
  'Austin, TX': 'Keep Austin Weird!',
  'Jacksonville, FL': 'Hello to our largest city!',
  'San Francisco, CA': 'Hello to the Golden Gate!',
  'Indianapolis, IN': 'Hello Hoosiers!',
  'Columbus, OH': 'Hello Cbus!',
  'Seattle, WA': 'Hello 12th man!',
  'Denver, CO': 'Hello Mile-High City!',
  'Washington, DC': 'Hello to the Nation’s Capital!',
  'Boston, MA': 'Hello Beantown!',
  'Atlanta, GA': 'Hello ATL!',
  'Miami, FL': 'Hello 305!',
}

const STATES: { [key: string]: string } = {
  AL: 'Hello sweet home Alabama!',  // Alabama
  AK: 'Hello to the land of midnight sun!', // Alaska
  AZ: 'Hello to the Grand Canyon State!',  // Arizona
  AR: 'Hello to the Natural State!',  // Arkanzas
  CA: 'Hello to the Golden State!', // California
  CO: 'Hello Rocky Mountain State!',  // Colorado
  CT: 'Hello Constitution State!', // Connecticut
  DE: 'Hello to the First State!',  // Delaware
  FL: 'Hello Sunshine State!', // Florida
  GA: 'Hello Peach State!',  // Georgia
  HI: 'Hello Aloha State!',  // Hawaii
  ID: 'Hello Gem State!',  // Idaho
  IL: 'Hello to the Land of Lincoln!',  // Illinois
  IN: 'Hello Hoosier State!', // Indiana
  IA: 'Hello Hawkeye State!',  // Iowa
  KS: 'Hello Sunflower State!',  // Kansas
  KY: 'Hello Bluegrass State!',  // Kentucky
  LA: 'Hello Bayou State!', // Louisiana
  ME: 'Hello Maine lobsters!',  // Maine
  MD: 'Hello Chesapeake State!',  // Maryland
  MA: 'Hello to the Spirit of America!', // Massachusetts
  MI: 'Hello to the Great Lakes State!',  // Michigan
  MN: 'Hello to the Land of 10,000 Lakes!',  // Minnesota
  MS: 'Hello Magnolia State!', // Mississippi
  MO: 'Hello Show-Me State!', // Missouri
  MT: 'Hello Big Sky!',  // Montana
  NE: 'Hello Cornhusker State!',  // Nebraska
  NV: 'Hello Silver State!',  // Nevada
  NH: 'Live Free or Die!', // New Hampshire
  NJ: 'Hello Garden State!',  // New Jersey
  NM: 'Hello Land of Enchantment!', // New Mexico
  NY: 'We live in New York too!', // New York
  NC: 'Hello Tar Heel State!',  // North Carolina
  ND: 'Hello Peace Garden State!',  // North Dakota
  OH: 'Hello Buckeye State!', // Ohio
  OK: 'Hello Sooner State!',  // Oklahoma
  OR: 'Hello Beaver State!',  // Oregon
  PA: 'Hello Keystone State!',  // Pennsylvania
  RI: 'Hello Ocean State!', // Rhode Island
  SC: 'Hello Palmetto State!',  // South Carolina
  SD: 'Hello Mount Rushmore State!',  // South Dakota
  TN: 'Hello Volunteer State!',  // Tennessee
  TX: 'Hello Lone Star State!',  // Texas
  UT: 'Hello Friendly State!',  // Utah
  VT: 'Hello Green Mountain State!', // Vermont
  VA: 'Hello to the Mother of States!', // Virginia
  WA: 'Hello Evergreen State',  // Washington
  WV: 'Hello Mountain State!', // West Virginia
  WI: 'Hello Badger State!', // Wisconsin
  WY: 'Hello Big Wyoming!',  // Wyoming
}

export function getCityStateLabel (city: City): string {
  return city.name + ', ' + city.state.code
}

export function getCityLabel (city: string | null): string | null {
  const REGEX = /^([^,]+), (\w\w)$/
  if (!city) {
    return null
  }
  const match = city.match(REGEX)
  if (!match) {
    return null
  }

  const [, cityName, stateCode] = match
  return BIG_CITIES[city] ||
    STATES[stateCode] ||
    `${ cityName } is on our list of places to visit!`
}
